/**
 * Omnisend Client-Side Tracking
 *
 * This module provides functions to track user behavior in Omnisend for
 * campaign and automation attribution.
 *
 * IMPORTANT: The Omnisend snippet in _document.tsx must load first.
 * These functions push to the window.omnisend array.
 *
 * Key functions:
 * - omnisendIdentify: Links web sessions to email contacts (CRITICAL for attribution)
 * - omnisendTrackProductViewed: Tracks product page views
 * - omnisendTrackAddedToCart: Tracks add to cart events
 * - omnisendTrackStartedCheckout: Tracks checkout initiation
 *
 * @see https://support.omnisend.com/en/articles/1061818-how-to-track-custom-events
 */

import { ProductDocument } from "@util/types/firestore/products";
import { CartItem } from "@util/types/firestore/carts";

const isProd = process.env.NEXT_PUBLIC_VERCEL_ENV === "production";

// Type for the Omnisend global array
type OmnisendCommand = [string, ...unknown[]];

declare global {
  interface Window {
    omnisend?: OmnisendCommand[];
  }
}

/**
 * Safely push a command to Omnisend.
 * Returns false if Omnisend isn't loaded or we're not in production.
 */
function pushToOmnisend(...args: OmnisendCommand): boolean {
  if (typeof window === "undefined") return false;
  if (!isProd) return false;

  // Ensure omnisend array exists
  window.omnisend = window.omnisend || [];
  window.omnisend.push(args);
  return true;
}

/**
 * Identify a user to Omnisend by email.
 *
 * This is CRITICAL for campaign attribution. When a user is identified,
 * Omnisend can connect their web session to their email contact, allowing
 * proper conversion tracking for campaigns and automations.
 *
 * Call this when:
 * - User logs in
 * - User signs up
 * - User enters email at checkout (for guests)
 *
 * @param email - User's email address
 * @param firstName - Optional first name
 * @param lastName - Optional last name
 * @param phone - Optional phone number
 */
export function omnisendIdentify(
  email: string,
  firstName?: string,
  lastName?: string,
  phone?: string
): boolean {
  if (!email) return false;

  const contactInfo: Record<string, string> = {
    email: email.toLowerCase().trim(),
  };

  if (firstName) contactInfo.firstName = firstName;
  if (lastName) contactInfo.lastName = lastName;
  if (phone) contactInfo.phone = phone;

  return pushToOmnisend("identify", contactInfo);
}

/**
 * Track a product view event.
 *
 * Omnisend uses this to:
 * - Build browse abandonment automations
 * - Track which products users view from email campaigns
 *
 * @param product - The product being viewed
 */
export function omnisendTrackProductViewed(product: ProductDocument): boolean {
  if (!product?.id) return false;

  return pushToOmnisend("track", "$productViewed", {
    $productID: product.id,
    $variantID: product.sku || product.id,
    $currency: "USD",
    $price: product.price,
    $title: product.title,
    $productURL: `https://gearfocus.com/products/${product.slug}`,
    $imageURL: product.thumbnail || product.images?.[0] || "",
    $vendor: product.brand || "",
    $description: product.description?.substring(0, 500) || "",
  });
}

/**
 * Track an add to cart event.
 *
 * Omnisend uses this for:
 * - Cart abandonment automations
 * - Campaign conversion attribution
 *
 * @param product - The product being added
 * @param quantity - Quantity added (default 1)
 */
export function omnisendTrackAddedToCart(
  product: ProductDocument,
  quantity: number = 1
): boolean {
  if (!product?.id) return false;

  return pushToOmnisend("track", "$productAdded", {
    $productID: product.id,
    $variantID: product.sku || product.id,
    $currency: "USD",
    $price: product.price,
    $title: product.title,
    $quantity: quantity,
    $productURL: `https://gearfocus.com/products/${product.slug}`,
    $imageURL: product.thumbnail || product.images?.[0] || "",
    $vendor: product.brand || "",
  });
}

/**
 * Track checkout started event.
 *
 * Omnisend uses this for:
 * - Checkout abandonment automations
 * - Campaign conversion attribution
 *
 * @param items - Cart items
 * @param total - Cart total
 * @param email - Optional email (for guest checkout identification)
 */
export function omnisendTrackStartedCheckout(
  items: CartItem[],
  total: number,
  email?: string
): boolean {
  // If we have an email, identify the user first
  if (email) {
    omnisendIdentify(email);
  }

  const cartItems = items.map((item) => ({
    $productID: item.product_id,
    $variantID: item.product_sku || item.product_id,
    $currency: "USD",
    $price: item.product_cost,
    $title: item.product_title,
    $quantity: item.qty || 1,
    $productURL: item.product_link || `https://gearfocus.com/products/${item.product_id}`,
    $imageURL: item.product_image || "",
    $vendor: item.product_brand || "",
  }));

  return pushToOmnisend("track", "$startedCheckout", {
    $value: total,
    $currency: "USD",
    $items: cartItems,
  });
}

/**
 * Track a custom event.
 *
 * Use this for custom automations like:
 * - Offer made
 * - Wishlist added
 * - Account created
 *
 * @param eventName - Name of the custom event
 * @param properties - Event properties
 */
export function omnisendTrackCustomEvent(
  eventName: string,
  properties: Record<string, unknown> = {}
): boolean {
  if (!eventName) return false;

  return pushToOmnisend("track", eventName, properties);
}

/**
 * Order item for Omnisend purchase tracking
 */
export type OmnisendOrderItem = {
  productId: string;
  sku?: string;
  title: string;
  price: number;
  quantity: number;
  imageUrl?: string;
  productUrl?: string;
  brand?: string;
};

/**
 * Track a completed order/purchase.
 *
 * Omnisend uses this for:
 * - Order confirmation automations
 * - Campaign conversion attribution
 * - Revenue reporting
 *
 * IMPORTANT: Call omnisendIdentify() before this if customer email is available
 * to ensure proper attribution.
 *
 * @param orderId - Unique order identifier
 * @param total - Order total (after discounts, before tax/shipping)
 * @param items - Array of items in the order
 * @param options - Optional: currency, tax, shipping, discount, email
 */
export function omnisendTrackPurchase(
  orderId: string,
  total: number,
  items: OmnisendOrderItem[],
  options?: {
    currency?: string;
    tax?: number;
    shipping?: number;
    discount?: number;
    email?: string;
  }
): boolean {
  if (!orderId || !items?.length) return false;

  // If we have an email, identify the user first for proper attribution
  if (options?.email) {
    omnisendIdentify(options.email);
  }

  const cartItems = items.map((item) => ({
    $productID: item.productId,
    $variantID: item.sku || item.productId,
    $currency: options?.currency || "USD",
    $price: item.price,
    $title: item.title,
    $quantity: item.quantity,
    $productURL: item.productUrl || `https://gearfocus.com/products/${item.productId}`,
    $imageURL: item.imageUrl || "",
    $vendor: item.brand || "",
  }));

  return pushToOmnisend("track", "$orderCompleted", {
    $orderID: orderId,
    $value: total,
    $currency: options?.currency || "USD",
    $items: cartItems,
    ...(typeof options?.tax === "number" ? { $tax: options.tax } : {}),
    ...(typeof options?.shipping === "number" ? { $shipping: options.shipping } : {}),
    ...(typeof options?.discount === "number" ? { $discount: options.discount } : {}),
  });
}
